import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';
import { config } from 'config';
import { useOrganizations } from '../useOrganizations';

export type ScopeTab = 'all' | 'account' | 'mine';
export type ExpandedTable = 'positive' | 'negative' | null;

function getExperimentMetrics(exp: ExperimentMonitorStatsRow) {
  let totalSessions = 0;
  let totalConversions = 0;
  let totalRevenue = 0;

  for (const variant of exp.variants) {
    totalSessions += variant.visitorCount?.count || 0;
  }
  for (const stat of exp.stats) {
    totalConversions += stat.conversions || 0;
    totalRevenue += stat.revenue || 0;
  }

  const conversionRate = totalSessions > 0 ? (totalConversions / totalSessions) * 100 : 0;
  const trend = conversionRate > 3.5 ? 'positive' : conversionRate < 2.5 ? 'negative' : null;

  return { totalSessions, totalConversions, totalRevenue, conversionRate, trend };
}

export const useExperimentsMonitor = () => {
  const statisticsClient = useClient(config.STATISTICS_URL);
  const { organization } = useOrganizations();

  // Local state
  const [search, setSearch] = useState('');
  const [scopeTab, setScopeTab] = useState<ScopeTab>('all');
  const [expandedTable, setExpandedTable] = useState<ExpandedTable>(null);

  // Fetch data
  const { data: experiments, isLoading } = useQuery<ExperimentMonitorStatsRow[]>({
    queryKey: ['EXPERIMENTS_MONITOR_LIST'],
    queryFn: () => statisticsClient.get(`/monitor`),
  });

  // Base experiments filtered by scope tab. 'mine' cuts across accounts: it is
  // a different axis from 'account', not a narrowing of it.
  const scopedExperiments = (() => {
    if (!experiments) return [];
    if (scopeTab === 'account' && organization) {
      return experiments.filter((exp) => exp.companyId === organization.id);
    }
    if (scopeTab === 'mine') {
      return experiments.filter((exp) => exp.isMine);
    }
    return experiments;
  })();

  // Further filtered by search
  const filteredExperiments = (() => {
    if (!scopedExperiments.length) return [];
    if (!search) return scopedExperiments;
    const query = search.toLowerCase();
    return scopedExperiments.filter(
      (exp) =>
        exp.experimentName.toLowerCase().includes(query) ||
        exp.websiteUrl.toLowerCase().includes(query) ||
        exp.variants.some((v) => v.name.toLowerCase().includes(query)),
    );
  })();

  // Separate positive and negative experiments (based on scoped data)
  const metricsManager = (() => {
    if (!scopedExperiments.length)
      return {
        positiveExperiments: [] as ExperimentMonitorStatsRow[],
        negativeExperiments: [] as ExperimentMonitorStatsRow[],
        activeCount: 0,
        positiveCount: 0,
        negativeCount: 0,
        positivePct: 0,
        negativePct: 0,
      };

    const positive: ExperimentMonitorStatsRow[] = [];
    const negative: ExperimentMonitorStatsRow[] = [];

    for (const exp of scopedExperiments) {
      const { trend } = getExperimentMetrics(exp);
      if (trend === 'positive') positive.push(exp);
      else if (trend === 'negative') negative.push(exp);
    }

    const total = scopedExperiments.length;
    return {
      positiveExperiments: positive,
      negativeExperiments: negative,
      activeCount: total,
      positiveCount: positive.length,
      negativeCount: negative.length,
      positivePct: total > 0 ? Math.round((positive.length / total) * 100) : 0,
      negativePct: total > 0 ? Math.round((negative.length / total) * 100) : 0,
    };
  })();
  const { positiveExperiments, negativeExperiments } = metricsManager;
  const { activeCount, positiveCount, negativeCount, positivePct, negativePct } = metricsManager;

  // Filter positive/negative based on search
  const filteredPositive = (() => {
    if (!search) return positiveExperiments;
    const query = search.toLowerCase();
    return positiveExperiments.filter(
      (exp) => exp.experimentName.toLowerCase().includes(query) || exp.websiteUrl.toLowerCase().includes(query),
    );
  })();

  const filteredNegative = (() => {
    if (!search) return negativeExperiments;
    const query = search.toLowerCase();
    return negativeExperiments.filter(
      (exp) => exp.experimentName.toLowerCase().includes(query) || exp.websiteUrl.toLowerCase().includes(query),
    );
  })();

  return {
    // Loading state
    isLoading,
    experiments,

    // Local state
    search,
    setSearch,
    scopeTab,
    setScopeTab,
    expandedTable,
    setExpandedTable,

    // Derived state
    filteredExperiments,
    filteredPositive,
    filteredNegative,
    activeCount,
    positiveCount,
    negativeCount,
    positivePct,
    negativePct,
  };
};

export type ExperimentsMonitorManager = ReturnType<typeof useExperimentsMonitor>;

export type VariantVisitorCount = {
  count: number;
};

export type ExperimentMonitorVariant = {
  id: number;
  name: string;
  weight: number | null;
  deployed: number | null;
  visitorCount: VariantVisitorCount;
};

export type ExperimentMonitorStat = {
  variantId: number;
  experimentId: number;
  currencyCode: string;
  conversions: number;
  revenue: number;
};

export type ExperimentMonitorStatsRow = {
  id: number;
  experimentId: number;
  experimentName: string;
  websiteId: number;
  websiteUrl: string;
  companyId: number | null;
  parentCompanyId: number | null;
  /** Whether the viewer created or started this experiment. Set per request. */
  isMine: boolean;
  variants: ExperimentMonitorVariant[];
  stats: ExperimentMonitorStat[];
  updatedAt: Date;
};
